import React, { useEffect, useRef, useState, useCallback } from 'react';
import supabase from '../config/supabase';
import { directQuery } from '../utils/directFetch';
import useHideMobileNav from '../utils/useHideMobileNav';
import VendorAutocompleteInput from './VendorAutocompleteInput';
import { getCachedVendorDirectory, getVendorMatchState } from '../utils/vendorResolution';
import {
  createOptionForItem,
  findInventoryItemAfterDuplicateMerge,
} from '../services/itemCrossReferenceService';

// Combine an OpenFoodFacts brand + product name into a single "Item Name"
// the way a chef would write it on a count sheet — e.g. "Tito's" + "Handmade
// Vodka" → "Tito's Handmade Vodka". Avoids duplicating the brand if it's
// already inside the product name.
function combineBrandAndName(brand, name) {
  const b = (brand || '').trim();
  const n = (name || '').trim();
  if (!b) return n;
  if (!n) return b;
  // Compare with punctuation/whitespace stripped so "Coca-Cola" + "Coca Cola Zero"
  // doesn't end up doubled. If the brand is already a prefix of the product
  // name (loosely), keep just the product name.
  const norm = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '');
  if (norm(n).startsWith(norm(b))) return n;
  return `${b} ${n}`;
}

const STOPWORDS = new Set([
  'the','a','an','of','and','or','with','for','to','in','on','by','from',
  'oz','lb','lbs','kg','g','ml','l','ct','pk','pack','case','each','ea','count',
  'fl','floz','gal','qt','pt'
]);

function tokenize(text) {
  if (!text) return [];
  return String(text)
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(t => t.length >= 2 && !STOPWORDS.has(t));
}

function scoreMatch(itemText, candidateTokens) {
  const itemTokens = new Set(tokenize(itemText));
  let overlap = 0;
  for (const t of candidateTokens) if (itemTokens.has(t)) overlap += 1;
  return overlap;
}

// Returns one of:
//   { online: true,  product: {...} }  → online + product found
//   { online: true,  product: null }   → online but UPC unknown to OFF
//   { online: false, product: null }   → request failed (treat as offline)
async function lookupOpenFoodFacts(upc) {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { online: false, product: null };
  }
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 6000);
    const res = await fetch(
      `https://world.openfoodfacts.org/api/v2/product/${encodeURIComponent(upc)}.json?fields=product_name,brands,quantity,categories`,
      { signal: ctrl.signal, cache: 'no-store' }
    );
    clearTimeout(t);
    if (!res.ok) {
      // 404 still means we reached the network — treat as online, no product.
      if (res.status === 404) return { online: true, product: null };
      return { online: false, product: null };
    }
    const json = await res.json();
    if (json.status !== 1 || !json.product) {
      return { online: true, product: null };
    }
    const p = json.product;
    return {
      online: true,
      product: {
        name: p.product_name || '',
        brand: (p.brands || '').split(',')[0]?.trim() || '',
        pack_size: p.quantity || '',
        category: (p.categories || '').split(',')[0]?.trim() || '',
      },
    };
  } catch {
    return { online: false, product: null };
  }
}

// Rank suggestions: exact match first, prefix match second, contains-match third.
// Returns at most 8 entries. Empty needle → first 8 (alphabetical from caller).
function rankSuggestions(list, needle) {
  if (!Array.isArray(list) || list.length === 0) return [];
  const n = String(needle || '').trim().toLowerCase();
  if (!n) return list.slice(0, 8);
  const exact = []; const prefix = []; const contains = [];
  for (const item of list) {
    const lower = item.toLowerCase();
    if (lower === n) exact.push(item);
    else if (lower.startsWith(n)) prefix.push(item);
    else if (lower.includes(n)) contains.push(item);
  }
  return [...exact, ...prefix, ...contains].slice(0, 8);
}

const EMPTY_FORM = {
  description: '', vendor: '', category: '', location: '',
  pack_size: '', unit: '', par_level: '',
  case_price: '', each_price: '',
};

const BarcodeScanAndAddModal = ({
  open,
  onClose,
  storeId,
  session = null,            // selectedSession — gives us order_calculations_enabled, count_purpose, inventory_mode
  countSessionId = null,
  countItems = null,
  onJumpToItem = null,        // (idx) => void  — jump to existing item
  onJumpToInventoryItem = null, // (inventoryItemId) => void  — jump after a refresh by inv id
  onItemLinked = null,
  onItemAdded = null,
  noScanMode = false,         // open directly in search/create flow, skip camera
  currentLocation = null,     // active room from the count tool — newly created
                              // items inherit this so they land in the user's
                              // current room instead of null/'Individual Items'.
  hideLocationField = false,  // mobile inline-count flow hides the Location
                              // input (Task #631): the active room is auto-
                              // applied via currentLocation, and re-typing on a
                              // small keyboard caused typos that fragmented the
                              // rooms strip. Desktop still shows the field.
}) => {
  const orderEnabled = !!session?.order_calculations_enabled;
  const isEOP = ((session?.count_purpose || '') + ' ' + (session?.inventory_mode || ''))
    .toLowerCase()
    .includes('eop');
  const showParMoq = orderEnabled;
  const showPrice = orderEnabled || isEOP;

  const scannerRef = useRef(null);
  const scannerDivId = useRef(`bsm-scanner-${Math.random().toString(36).slice(2)}`);
  const handlingScanRef = useRef(false);
  const [phase, setPhase] = useState(noScanMode ? 'searchOrCreate' : 'scanning');
  const [scannedCode, setScannedCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [info, setInfo] = useState('');
  const [offMatch, setOffMatch] = useState(null);
  const [online, setOnline] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_FORM);
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [knownCategories, setKnownCategories] = useState([]);
  const [knownLocations, setKnownLocations] = useState([]);
  const [knownUnits, setKnownUnits] = useState([]);
  const [knownPackSizes, setKnownPackSizes] = useState([]);
  // Which suggestion dropdown is currently open: 'category' | 'unit' | 'pack_size' | 'location' | null
  const [activeField, setActiveField] = useState(null);

  const reset = useCallback(() => {
    setPhase(noScanMode ? 'searchOrCreate' : 'scanning'); setScannedCode(''); setBusy(false);
    setErr(''); setInfo(''); setOffMatch(null); setSuggestions([]);
    setSearchText(''); setSearchResults([]);
    setCreateForm(EMPTY_FORM);
    setTorchOn(false);
  }, [noScanMode]);

  // Pull a small set of distinct categories + locations once per open, for
  // datalist suggestions on the create form (no UI for "manage" — autocomplete only).
  useEffect(() => {
    if (!open || !storeId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from('inventory_items')
          .select('category, location, unit, pack_size')
          .eq('store_id', storeId)
          .limit(2000);
        if (cancelled || !data) return;
        // Case-insensitive dedup: keep the first canonical spelling we see
        // for each normalized key so "Liquor" and "liquor" don't both appear.
        const dedup = (values) => {
          const seen = new Map();
          for (const v of values) {
            if (!v) continue;
            const trimmed = String(v).trim();
            if (!trimmed) continue;
            const key = trimmed.toLowerCase();
            if (!seen.has(key)) seen.set(key, trimmed);
          }
          return [...seen.values()].sort((a, b) => a.localeCompare(b));
        };
        setKnownCategories(dedup(data.map(r => r.category)));
        setKnownLocations(dedup(data.map(r => r.location)));
        setKnownUnits(dedup(data.map(r => r.unit)));
        setKnownPackSizes(dedup(data.map(r => r.pack_size)));
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [open, storeId]);

  useEffect(() => {
    if (!open) {
      if (scannerRef.current) {
        try { scannerRef.current.stop().catch(() => {}); } catch {}
        try { scannerRef.current.clear(); } catch {}
        scannerRef.current = null;
      }
      reset();
    }
  }, [open, reset]);

  // Start scanner when in scanning phase
  useEffect(() => {
    if (!open || phase !== 'scanning') return;
    let cancelled = false;
    let instance = null;
    let videoObserver = null;
    // Watch the scanner container; the moment html5-qrcode (or any internal
    // wrapper) inserts a <video>, force the attributes/styles a browser needs
    // to autoplay inline silently. Without this, mobile Safari/Chrome briefly
    // shows its native poster + ▶ play overlay, hiding the targeting reticle.
    //
    // NOTE (Task #579): When this scanner runs inside the Expo mobile shell
    // (WKWebView), the *real* fix for the "native iOS camera replaces our
    // overlay" bug lives in apps/mobile/src/screens/WrappedWebScreen.js —
    // specifically the `allowsInlineMediaPlayback`,
    // `mediaPlaybackRequiresUserAction={false}`, and
    // `mediaCapturePermissionGrantDecision="grant"` props plus the iOS
    // `NSCameraUsageDescription` and Android `CAMERA` permission in
    // apps/mobile/app.json. This MutationObserver block is belt-and-braces
    // for plain mobile-Safari testing only — do not re-debug this at the
    // web layer if the overlay vanishes inside the native app.
    const styleVideo = (root) => {
      if (!root) return;
      root.querySelectorAll('video').forEach((v) => {
        try {
          v.muted = true;
          v.defaultMuted = true;
          v.autoplay = true;
          v.playsInline = true;
          v.setAttribute('muted', '');
          v.setAttribute('autoplay', '');
          v.setAttribute('playsinline', '');
          v.setAttribute('webkit-playsinline', '');
          v.removeAttribute('controls');
          v.controls = false;
          v.style.width = '100%';
          v.style.height = '100%';
          v.style.objectFit = 'cover';
          v.style.display = 'block';
          v.style.background = '#000';
          const p = v.play && v.play();
          if (p && typeof p.catch === 'function') p.catch(() => {});
        } catch {}
      });
    };
    const containerEl = document.getElementById(scannerDivId.current);
    if (containerEl) {
      // Paint a dark backdrop immediately so the white area between the
      // overlay corner brackets isn't visible while the camera spins up.
      containerEl.style.background = '#000';
      videoObserver = new MutationObserver(() => styleVideo(containerEl));
      videoObserver.observe(containerEl, { childList: true, subtree: true });
    }
    (async () => {
      try {
        const mod = await import('html5-qrcode');
        if (cancelled) return;
        const { Html5Qrcode, Html5QrcodeSupportedFormats } = mod;
        instance = new Html5Qrcode(scannerDivId.current, {
          formatsToSupport: [
            Html5QrcodeSupportedFormats.UPC_A,
            Html5QrcodeSupportedFormats.UPC_E,
            Html5QrcodeSupportedFormats.EAN_13,
            Html5QrcodeSupportedFormats.EAN_8,
            Html5QrcodeSupportedFormats.CODE_128,
          ],
          verbose: false,
        });
        scannerRef.current = instance;
        await instance.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 280, height: 140 }, aspectRatio: 1.6 },
          (decodedText) => {
            const code = String(decodedText || '').replace(/\D/g, '');
            if (!code) return;
            handleScanned(code);
          },
          () => {}
        );
        // Force-style the video element as soon as html5-qrcode finishes
        // attaching it (the observer above usually catches it first, but
        // this guarantees it on slow renders too).
        styleVideo(containerEl);
        // Detect torch capability on the active video track.
        try {
          const tracks = instance.getRunningTrackCapabilities ? null : null;
          // html5-qrcode exposes the underlying stream via getState/etc., but
          // the simplest cross-version path is to read the live MediaStream
          // off the first <video> we placed in our scanner div.
          const videoEl = document.querySelector(`#${scannerDivId.current} video`);
          const stream = videoEl?.srcObject;
          const track = stream?.getVideoTracks?.()[0];
          const caps = track?.getCapabilities?.();
          if (caps && 'torch' in caps) setTorchSupported(true);
        } catch {}
      } catch (e) {
        if (!cancelled) setErr('Could not start camera: ' + (e?.message || e));
      }
    })();
    return () => {
      cancelled = true;
      if (videoObserver) { try { videoObserver.disconnect(); } catch {} }
      const inst = scannerRef.current;
      if (inst) {
        try { inst.stop().then(() => { try { inst.clear(); } catch {} }).catch(() => {}); } catch {}
        scannerRef.current = null;
      }
      setTorchSupported(false);
      setTorchOn(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, phase]);

  const toggleTorch = async () => {
    try {
      const inst = scannerRef.current;
      if (!inst) return;
      const next = !torchOn;
      await inst.applyVideoConstraints({ advanced: [{ torch: next }] });
      setTorchOn(next);
    } catch (e) {
      setErr('Torch unavailable on this device.');
    }
  };

  const handleScanned = async (code) => {
    if (!code || handlingScanRef.current) return;
    handlingScanRef.current = true;
    setBusy(true);
    const inst = scannerRef.current;
    if (inst) { try { await inst.stop(); inst.clear(); } catch {} scannerRef.current = null; }
    setScannedCode(code);
    setPhase('processing');
    setErr(''); setInfo('Looking up in your inventory…');
    try {
      // Exact lookup by supc OR manufacturer_code, scoped to store.
      // Try combined .or() first; if it fails (e.g. one column missing on
      // some deployments), fall back to two single-column queries.
      let exact = null;
      try {
        const { data, error } = await supabase
          .from('inventory_items')
          .select('*')
          .eq('store_id', storeId)
          .or(`supc.eq.${code},manufacturer_code.eq.${code}`)
          .limit(1);
        if (!error && data && data.length > 0) exact = data[0];
        if (error) throw error;
      } catch {
        for (const col of ['supc', 'manufacturer_code']) {
          if (exact) break;
          try {
            const { data } = await supabase
              .from('inventory_items')
              .select('*')
              .eq('store_id', storeId)
              .eq(col, code)
              .limit(1);
            if (data && data.length > 0) exact = data[0];
          } catch {}
        }
      }

      if (exact) {
        // Already in current count?
        if (countItems && Array.isArray(countItems)) {
          const idx = countItems.findIndex(ci =>
            (ci.inventory_item_id && String(ci.inventory_item_id) === String(exact.id))
            || (ci.item_snapshot && String(ci.item_snapshot.id) === String(exact.id))
          );
          if (idx >= 0) {
            setInfo(`Already in this count: ${exact.description || exact.name}`);
            setPhase('inCount');
            setOffMatch({ inventory_item: exact, indexInCount: idx });
            setBusy(false);
            return;
          }
        }
        setOffMatch({ inventory_item: exact });
        setPhase('exactMatch');
        setBusy(false);
        return;
      }

      // Not in inventory. Try OpenFoodFacts; the lookup itself tells us if
      // we're really online (no separate probe — the WebView blocks many
      // arbitrary cross-origin pings, which made the probe always fail).
      setInfo('Looking up product database…');
      const lookup = await lookupOpenFoodFacts(code);
      setOnline(lookup.online);
      const off = lookup.product;
      setOffMatch(off);

      // Build fuzzy suggestions if we have a name from OFF.
      if (off && off.name) {
        const tokens = tokenize(`${off.name} ${off.brand}`);
        const { data: invList } = await supabase
          .from('inventory_items')
          .select('id, description, brand, vendor, supc, manufacturer_code, pack_size, unit, category, location')
          .eq('store_id', storeId)
          .limit(2000);
        const scored = (invList || [])
          .map(it => ({
            it,
            score: scoreMatch(`${it.description || ''} ${it.brand || ''}`, tokens),
          }))
          .filter(s => s.score >= 2)
          .sort((a, b) => b.score - a.score)
          .slice(0, 8)
          .map(s => s.it);
        setSuggestions(scored);
        setCreateForm(f => ({
          ...EMPTY_FORM,
          description: combineBrandAndName(off.brand, off.name),
          pack_size: off.pack_size || '',
          category: off.category || '',
        }));
        setPhase(scored.length > 0 ? 'suggest' : 'searchOrCreate');
      } else {
        // Offline OR OFF returned nothing — go straight to search/create.
        setCreateForm(EMPTY_FORM);
        setPhase('searchOrCreate');
      }
    } catch (e) {
      setErr('Lookup error: ' + (e?.message || e));
      setPhase('searchOrCreate');
    } finally {
      setBusy(false);
      handlingScanRef.current = false;
      setInfo('');
    }
  };

  // Manual search of all inventory items (only triggered from searchOrCreate)
  useEffect(() => {
    if (phase !== 'searchOrCreate') return;
    const q = searchText.trim();
    if (q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    const handle = setTimeout(async () => {
      try {
        const needle = q.toLowerCase();
        const { data } = await supabase
          .from('inventory_items')
          .select('id, description, brand, vendor, supc, manufacturer_code, pack_size, unit, category, location')
          .eq('store_id', storeId)
          .or(`description.ilike.%${needle}%,brand.ilike.%${needle}%,vendor.ilike.%${needle}%,category.ilike.%${needle}%`)
          .limit(40);
        setSearchResults(data || []);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [searchText, phase, storeId]);

  const linkUpcToItem = async (item) => {
    setBusy(true); setErr('');
    try {
      // No-scan mode: no UPC to link — just add the picked existing item to count.
      if (!scannedCode) {
        if (countSessionId) {
          await addItemToCurrentCount(item);
        } else {
          finishWith(`Selected "${item.description || item.name}"`);
        }
        return;
      }
      const update = {};
      if ('supc' in item || item.supc !== undefined) update.supc = scannedCode;
      update.manufacturer_code = scannedCode;
      const { error } = await directQuery('inventory_items', { update, eq: { id: item.id } });
      if (error) throw error;
      const linked = { ...item, supc: scannedCode, manufacturer_code: scannedCode };
      onItemLinked && onItemLinked(linked);
      if (countSessionId) {
        await addItemToCurrentCount(linked);
      } else {
        finishWith(`Linked UPC ${scannedCode} to "${item.description || item.name}"`);
      }
    } catch (e) {
      setErr('Could not link: ' + (e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  const numOrNull = (v) => {
    const s = String(v ?? '').trim();
    if (s === '') return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  };

  const createNewItem = async () => {
    const name = createForm.description.trim();
    if (!name) { setErr('Item name is required'); return; }
    setBusy(true); setErr('');
    try {
      // Canonicalize category/unit/pack_size against the known set so a typed
      // "liquor" re-uses the existing "Liquor" spelling instead of fragmenting.
      const canonicalize = (raw, list) => {
        const trimmed = (raw || '').trim();
        if (!trimmed) return null;
        const hit = list.find(x => x.toLowerCase() === trimmed.toLowerCase());
        return hit || trimmed;
      };
      const resolvedCategory = canonicalize(createForm.category, knownCategories);
      const resolvedUnit = canonicalize(createForm.unit, knownUnits);
      const resolvedPackSize = canonicalize(createForm.pack_size, knownPackSizes);

      // Canonicalize vendor against the vendor directory: if the typed value
      // matches an existing vendor or alias (case-insensitive), snap to the
      // canonical display name and set vendor_id so no duplicate is created.
      let resolvedVendor = createForm.vendor?.trim() || null;
      let resolvedVendorId = null;
      if (resolvedVendor) {
        try {
          const directory = await getCachedVendorDirectory({ supabaseClient: supabase, storeId, forceRefresh: false });
          const matchState = getVendorMatchState(directory, resolvedVendor);
          if (matchState.state === 'existing' || matchState.state === 'alias') {
            resolvedVendor = matchState.vendorDisplayName;
            resolvedVendorId = matchState.vendorId || null;
          }
        } catch {}
      }

      const newRow = {
        store_id: storeId,
        description: name,
        vendor: resolvedVendor,
        category: resolvedCategory,
        location: createForm.location?.trim() || (currentLocation ? String(currentLocation).trim() : null),
        pack_size: resolvedPackSize,
        unit: resolvedUnit,
      };
      if (resolvedVendorId) {
        newRow.vendor_id = resolvedVendorId;
      }
      if (scannedCode) {
        newRow.supc = scannedCode;
        newRow.manufacturer_code = scannedCode;
      }
      if (showParMoq) {
        newRow.par_level = numOrNull(createForm.par_level);
        newRow.par_unit = resolvedUnit || 'each';
      }
      if (showPrice) {
        newRow.case_price = numOrNull(createForm.case_price);
        newRow.each_price = numOrNull(createForm.each_price);
      }
      const { data: inserted, error } = await supabase
        .from('inventory_items')
        .insert([newRow])
        .select()
        .maybeSingle();
      if (error) throw error;
      const mergedByDuplicateTrigger = !inserted?.id;
      const data = inserted || await findInventoryItemAfterDuplicateMerge(
        storeId,
        newRow,
        {},
        supabase,
      );
      if (!data?.id) throw new Error('The saved inventory item could not be resolved.');

      // Give the new item its first way to buy it, the same as every other
      // add-an-item channel. Without this a scanned item sits outside the
      // multi-vendor model: no comparable unit cost, and the first invoice to
      // mention it would overwrite its price instead of adding a vendor.
      // Best-effort — the item is already saved and must not be lost to this.
      try {
        await createOptionForItem(storeId, {
          ...data,
          ...newRow,
          id: data.id,
          unit_price: newRow.each_price ?? data.each_price ?? data.unit_price ?? null,
        }, { channel: 'barcode_scan' });
      } catch (optionErr) {
        console.warn('[barcode-add] Purchase option mirror failed (non-fatal):', optionErr?.message);
      }

      if (countSessionId) {
        await addItemToCurrentCount(data);
      } else {
        onItemAdded && onItemAdded(data);
        finishWith(mergedByDuplicateTrigger
          ? `Updated the existing "${data.description}" and its vendor product.`
          : `Added "${data.description}" to inventory.`);
      }
    } catch (e) {
      setErr('Could not create: ' + (e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  const addItemToCurrentCount = async (invItem) => {
    try {
      const maxPos = countItems && countItems.length > 0
        ? Math.max(...countItems.map(c => Number(c.count_position) || 0))
        : 0;
      // Guard: avoid duplicating an item that's already in this count.
      const already = (countItems || []).find(ci =>
        (ci.inventory_item_id && String(ci.inventory_item_id) === String(invItem.id))
        || (ci.item_snapshot && String(ci.item_snapshot.id) === String(invItem.id))
      );
      if (already) {
        finishWith(`"${invItem.description}" is already in this count.`);
        return;
      }
      const row = {
        count_session_id: countSessionId,
        inventory_item_id: invItem.id,
        item_snapshot: {
          id: invItem.id,
          description: invItem.description,
          name: invItem.description,
          brand: invItem.brand,
          vendor: invItem.vendor,
          vendor_name: invItem.vendor,
          category: invItem.category,
          location: invItem.location,
          pack_size: invItem.pack_size,
          unit: invItem.unit,
          supc: invItem.supc,
          manufacturer_code: invItem.manufacturer_code,
          par_level: invItem.par_level ?? null,
          par_unit: invItem.par_unit || 'each',
        },
        expected_quantity: '',
        actual_quantity: '',
        variance_quantity: 0,
        count_notes: '',
        is_counted: false,
        counted_at: null,
        par_level: invItem.par_level ?? null,
        par_unit: invItem.par_unit || 'each',
        location: invItem.location || 'Individual Items',
        count_position: maxPos + 10,
      };
      const { data, error } = await supabase
        .from('count_items')
        .insert([row])
        .select()
        .single();
      if (error) throw error;
      // Tell the parent which inventory item we just landed on so it can
      // refresh the count list, find the new row, and jump the user there.
      onItemAdded && onItemAdded(data, invItem);
      if (onJumpToInventoryItem) {
        try { await onJumpToInventoryItem(invItem.id); } catch {}
        if (onClose) onClose();
        return;
      }
      finishWith(`Added "${invItem.description}" to this count.`);
    } catch (e) {
      setErr('Added to inventory but could not add to count: ' + (e?.message || e));
    }
  };

  const finishWith = (msg) => {
    setInfo(msg);
    setPhase('done');
  };

  const jumpAndClose = (idx) => {
    onJumpToItem && onJumpToItem(idx);
    onClose && onClose();
  };

  useHideMobileNav(!!open);

  if (!open) return null;

  const overlayStyle = {
    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)',
    zIndex: 10000, display: 'flex', flexDirection: 'column', color: '#fff',
  };
  const sheetStyle = {
    backgroundColor: '#FEFCFA', color: '#3E2723', borderTopLeftRadius: 14,
    borderTopRightRadius: 14, padding: '14px 14px 18px', maxHeight: '92vh',
    overflowY: 'auto', boxShadow: '0 -6px 18px rgba(0,0,0,0.4)',
  };
  const btnPrimary = {
    backgroundColor: '#7B5E45', color: '#fff', border: 'none',
    borderRadius: 6, padding: '8px 14px', fontWeight: 600, fontSize: '0.85rem',
  };
  const btnGhost = {
    backgroundColor: 'transparent', color: '#5A4A42',
    border: '1px solid #C8B5A0', borderRadius: 6, padding: '6px 12px',
    fontSize: '0.8rem',
  };

  return (
    <div style={overlayStyle} role="dialog" aria-modal="true">
      {/* Camera viewport */}
      {phase === 'scanning' && (
        <div style={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
          <div id={scannerDivId.current} style={{ width: '100%', maxWidth: 480, aspectRatio: '4 / 3' }} />

          {/* Top bar */}
          <div style={{ position: 'absolute', top: 'calc(env(safe-area-inset-top, 0px) + 14px)', left: 'calc(env(safe-area-inset-left, 0px) + 14px)', right: 'calc(env(safe-area-inset-right, 0px) + 14px)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 2 }}>
            <span style={{ fontSize: '0.85rem', opacity: 0.9, backgroundColor: 'rgba(0,0,0,0.45)', padding: '4px 8px', borderRadius: 4 }}>
              Point at a barcode
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              {torchSupported && (
                <button onClick={toggleTorch}
                  style={{ ...btnGhost, color: '#fff', borderColor: '#fff', backgroundColor: torchOn ? 'rgba(255,200,0,0.25)' : 'transparent' }}>
                  {torchOn ? 'Light: On' : 'Light'}
                </button>
              )}
              <button onClick={() => { setPhase('searchOrCreate'); setScannedCode(''); }} style={{ ...btnGhost, color: '#fff', borderColor: '#fff' }}>
                Enter UPC
              </button>
              <button onClick={onClose} style={{ ...btnGhost, color: '#fff', borderColor: '#fff' }}>Close</button>
            </div>
          </div>

          {/* Scan reticle: corner brackets + dimmed surround */}
          <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ position: 'relative', width: 'min(86vw, 320px)', height: 'min(40vw, 160px)' }}>
              {[
                { top: 0, left: 0, borderTop: '3px solid #fff', borderLeft: '3px solid #fff' },
                { top: 0, right: 0, borderTop: '3px solid #fff', borderRight: '3px solid #fff' },
                { bottom: 0, left: 0, borderBottom: '3px solid #fff', borderLeft: '3px solid #fff' },
                { bottom: 0, right: 0, borderBottom: '3px solid #fff', borderRight: '3px solid #fff' },
              ].map((s, i) => (
                <div key={i} style={{ position: 'absolute', width: 28, height: 28, borderRadius: 4, ...s }} />
              ))}
              <div style={{ position: 'absolute', left: 0, right: 0, top: '50%', height: 2, backgroundColor: 'rgba(255,80,80,0.85)', boxShadow: '0 0 6px rgba(255,80,80,0.9)' }} />
            </div>
          </div>

          {/* Hint */}
          <div style={{ position: 'absolute', bottom: 'calc(env(safe-area-inset-bottom, 0px) + 18px)', left: 'env(safe-area-inset-left, 0px)', right: 'env(safe-area-inset-right, 0px)', textAlign: 'center', fontSize: '0.78rem', color: '#fff', opacity: 0.85, zIndex: 2 }}>
            Place the barcode inside the box
          </div>

          {err && (
            <div style={{ position: 'absolute', bottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)', left: 'calc(env(safe-area-inset-left, 0px) + 16px)', right: 'calc(env(safe-area-inset-right, 0px) + 16px)', backgroundColor: '#7a1f1f', padding: '10px 12px', borderRadius: 6, fontSize: '0.82rem', zIndex: 3 }}>
              {err}
              <div style={{ marginTop: 8 }}>
                <button onClick={() => { setErr(''); setPhase('searchOrCreate'); }} style={btnPrimary}>Enter manually</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Bottom sheet for all post-scan states */}
      {phase !== 'scanning' && (
        <div style={{ marginTop: 'auto' }}>
          <div style={sheetStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              {scannedCode ? (
                <div>
                  <div style={{ fontSize: '0.7rem', opacity: 0.6 }}>Scanned</div>
                  <div style={{ fontFamily: 'monospace', fontSize: '0.95rem', fontWeight: 700 }}>{scannedCode}</div>
                </div>
              ) : (
                <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#5A4A42' }}>Add item to this count</div>
              )}
              <button onClick={onClose} style={btnGhost}>Close</button>
            </div>

            {info && <div style={{ fontSize: '0.85rem', marginBottom: 8 }}>{info}</div>}
            {err && <div style={{ backgroundColor: '#fde2e2', color: '#7a1f1f', padding: '8px 10px', borderRadius: 6, fontSize: '0.82rem', marginBottom: 8 }}>{err}</div>}
            {phase !== 'done' && !online && (
              <div style={{ backgroundColor: '#fff5e0', color: '#6b4a00', padding: '8px 10px', borderRadius: 6, fontSize: '0.78rem', marginBottom: 8 }}>
                Offline — product-name lookup is unavailable. Search your items below or create one.
              </div>
            )}

            {/* In-count: jump */}
            {phase === 'inCount' && offMatch?.inventory_item && (
              <div>
                <div style={{ marginBottom: 10, fontWeight: 600 }}>{offMatch.inventory_item.description}</div>
                <button style={btnPrimary} disabled={busy} onClick={() => jumpAndClose(offMatch.indexInCount)}>
                  Jump to this item
                </button>
              </div>
            )}

            {/* Exact UPC match in inventory but not in count */}
            {phase === 'exactMatch' && offMatch?.inventory_item && (
              <div>
                <div style={{ fontSize: '0.78rem', opacity: 0.7 }}>Found in your inventory</div>
                <div style={{ fontWeight: 600, marginBottom: 10 }}>{offMatch.inventory_item.description}</div>
                {countSessionId ? (
                  <button style={btnPrimary} disabled={busy} onClick={() => addItemToCurrentCount(offMatch.inventory_item)}>
                    Add to this count
                  </button>
                ) : (
                  <button style={btnPrimary} onClick={onClose}>Done</button>
                )}
              </div>
            )}

            {/* OFF gave a name + we have ≥2-word matches */}
            {phase === 'suggest' && (
              <div>
                <div style={{ fontSize: '0.78rem', opacity: 0.7 }}>Product label says</div>
                <div style={{ fontWeight: 600, marginBottom: 10 }}>{offMatch?.name}</div>
                <div style={{ fontSize: '0.8rem', marginBottom: 6 }}>Likely matches in your inventory:</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                  {suggestions.map(s => (
                    <button key={s.id} disabled={busy} onClick={() => linkUpcToItem(s)}
                      style={{ textAlign: 'left', padding: '8px 10px', border: '1px solid #E8DDD0', borderRadius: 6, backgroundColor: '#fff' }}>
                      <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{s.description}</div>
                      <div style={{ fontSize: '0.72rem', opacity: 0.7 }}>
                        {[s.brand, s.pack_size, s.vendor].filter(Boolean).join(' · ')}
                      </div>
                    </button>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button style={btnGhost} onClick={() => setPhase('searchOrCreate')}>None of these — search all items</button>
                  <button style={btnPrimary} disabled={busy} onClick={createNewItem}>Create new "{createForm.description}"</button>
                </div>
              </div>
            )}

            {/* Manual search + create */}
            {phase === 'searchOrCreate' && (
              <div>
                {offMatch?.name && (
                  <div style={{ fontSize: '0.78rem', opacity: 0.7, marginBottom: 6 }}>
                    Product label says: <strong>{offMatch.name}</strong>
                  </div>
                )}
                <div style={{ fontSize: '0.8rem', marginBottom: 6 }}>{scannedCode ? 'Search your inventory to link this UPC:' : 'Search your inventory and pick an item to add:'}</div>
                <input
                  autoFocus
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Search by name, brand, vendor…"
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #C8B5A0', borderRadius: 6, fontSize: '0.85rem', marginBottom: 8 }}
                />
                {searching && <div style={{ fontSize: '0.75rem', opacity: 0.6, marginBottom: 6 }}>Searching…</div>}
                {searchResults.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: '38vh', overflowY: 'auto', marginBottom: 12 }}>
                    {searchResults.map(s => (
                      <button key={s.id} disabled={busy} onClick={() => linkUpcToItem(s)}
                        style={{ textAlign: 'left', padding: '8px 10px', border: '1px solid #E8DDD0', borderRadius: 6, backgroundColor: '#fff' }}>
                        <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{s.description}</div>
                        <div style={{ fontSize: '0.72rem', opacity: 0.7 }}>
                          {[s.brand, s.pack_size, s.vendor].filter(Boolean).join(' · ')}
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                <div style={{ marginTop: 8, paddingTop: 10, borderTop: '1px solid #E8DDD0' }}>
                  <div style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: 6 }}>{scannedCode ? 'Or create a new item with this UPC' : 'Or create a new item'}</div>

                  {/* Single Item Name field — combined brand + product */}
                  <label style={{ display: 'block', fontSize: '0.72rem', color: '#5A4A42', marginBottom: 2 }}>Item name</label>
                  <input
                    type="text"
                    value={createForm.description}
                    onChange={(e) => setCreateForm(f => ({ ...f, description: e.target.value }))}
                    placeholder={'e.g. "Tito\'s Handmade Vodka"'}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #C8B5A0', borderRadius: 6, fontSize: '0.9rem', marginBottom: 8 }}
                  />

                  {/* Vendor (autocomplete + create) */}
                  <label style={{ display: 'block', fontSize: '0.72rem', color: '#5A4A42', marginBottom: 2 }}>Vendor</label>
                  <div style={{ marginBottom: 8 }}>
                    <VendorAutocompleteInput
                      value={createForm.vendor}
                      onChange={(v) => setCreateForm(f => ({ ...f, vendor: v }))}
                      storeId={storeId}
                      placeholder="Search or add a vendor"
                    />
                  </div>

                  {/* Category + Location — Location is hidden in mobile inline-
                      count flow (Task #631): the active room is auto-applied via
                      currentLocation, and re-typing it on a small keyboard caused
                      typos that fragmented the rooms strip. Desktop keeps both. */}
                  <div style={{ display: 'grid', gridTemplateColumns: hideLocationField ? '1fr' : '1fr 1fr', gap: 6, marginBottom: 8 }}>
                    <div style={{ position: 'relative' }}>
                      <label style={{ display: 'block', fontSize: '0.72rem', color: '#5A4A42', marginBottom: 2 }}>Category</label>
                      <input type="text"
                        value={createForm.category}
                        onChange={(e) => setCreateForm(f => ({ ...f, category: e.target.value }))}
                        onFocus={() => setActiveField('category')}
                        onBlur={() => setTimeout(() => setActiveField(a => a === 'category' ? null : a), 150)}
                        placeholder="e.g. Liquor"
                        autoComplete="off" autoCorrect="off" autoCapitalize="words" spellCheck={false}
                        style={{
                          width: '100%', padding: '7px 9px',
                          border: activeField === 'category' ? '2px solid #8B6F47' : '1px solid #C8B5A0',
                          borderRadius: 6, fontSize: '0.82rem', outline: 'none',
                        }} />
                      {activeField === 'category' && (() => {
                        const ranked = rankSuggestions(knownCategories, createForm.category);
                        const typed = (createForm.category || '').trim();
                        const hasExact = typed && knownCategories.some(c => c.toLowerCase() === typed.toLowerCase());
                        if (ranked.length === 0 && !typed) return null;
                        return (
                          <div style={{
                            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                            background: '#FFF8EE', border: '1px solid #C8B5A0', borderTop: 'none',
                            borderRadius: '0 0 6px 6px', maxHeight: 220, overflowY: 'auto',
                            boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
                          }}>
                            {ranked.map(c => (
                              <div key={c}
                                onMouseDown={(e) => { e.preventDefault(); setCreateForm(f => ({ ...f, category: c })); setActiveField(null); }}
                                style={{ padding: '8px 10px', fontSize: '0.84rem', cursor: 'pointer', borderBottom: '1px solid #EDE3D2', color: '#3A2E26' }}>
                                {c}
                              </div>
                            ))}
                            {typed && !hasExact && (
                              <div onMouseDown={(e) => { e.preventDefault(); setActiveField(null); }}
                                style={{ padding: '8px 10px', fontSize: '0.8rem', color: '#A0612A', fontStyle: 'italic', cursor: 'pointer' }}>
                                + Use "{typed}" as new category
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      {activeField !== 'category' && (() => {
                        const v = (createForm.category || '').trim();
                        if (!v) return null;
                        const match = knownCategories.some(c => c.toLowerCase() === v.toLowerCase());
                        return (
                          <div style={{ fontSize: '0.68rem', marginTop: 3, color: match ? '#2F7A3E' : '#A0612A' }}>
                            {match ? '✓ Matches existing category' : '+ Will create new category'}
                          </div>
                        );
                      })()}
                    </div>
                    {!hideLocationField && (
                      <div>
                        <label style={{ display: 'block', fontSize: '0.72rem', color: '#5A4A42', marginBottom: 2 }}>Location</label>
                        <input type="text" list={`bsm-locs-${scannerDivId.current}`}
                          value={createForm.location}
                          onChange={(e) => setCreateForm(f => ({ ...f, location: e.target.value }))}
                          placeholder="e.g. Bar"
                          style={{ width: '100%', padding: '7px 9px', border: '1px solid #C8B5A0', borderRadius: 6, fontSize: '0.82rem' }} />
                        <datalist id={`bsm-locs-${scannerDivId.current}`}>
                          {knownLocations.map(l => <option key={l} value={l} />)}
                        </datalist>
                      </div>
                    )}
                  </div>

                  {/* Pack/Size + Unit */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 8 }}>
                    <div style={{ position: 'relative' }}>
                      <label style={{ display: 'block', fontSize: '0.72rem', color: '#5A4A42', marginBottom: 2 }}>Pack/Size</label>
                      <input type="text"
                        value={createForm.pack_size}
                        onChange={(e) => setCreateForm(f => ({ ...f, pack_size: e.target.value }))}
                        onFocus={() => setActiveField('pack_size')}
                        onBlur={() => setTimeout(() => setActiveField(a => a === 'pack_size' ? null : a), 150)}
                        placeholder="e.g. 750 ml"
                        autoComplete="off" autoCorrect="off" autoCapitalize="none" spellCheck={false}
                        style={{
                          width: '100%', padding: '7px 9px',
                          border: activeField === 'pack_size' ? '2px solid #8B6F47' : '1px solid #C8B5A0',
                          borderRadius: 6, fontSize: '0.82rem', outline: 'none',
                        }} />
                      {activeField === 'pack_size' && (() => {
                        const ranked = rankSuggestions(knownPackSizes, createForm.pack_size);
                        const typed = (createForm.pack_size || '').trim();
                        const hasExact = typed && knownPackSizes.some(p => p.toLowerCase() === typed.toLowerCase());
                        if (ranked.length === 0 && !typed) return null;
                        return (
                          <div style={{
                            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                            background: '#FFF8EE', border: '1px solid #C8B5A0', borderTop: 'none',
                            borderRadius: '0 0 6px 6px', maxHeight: 220, overflowY: 'auto',
                            boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
                          }}>
                            {ranked.map(p => (
                              <div key={p}
                                onMouseDown={(e) => { e.preventDefault(); setCreateForm(f => ({ ...f, pack_size: p })); setActiveField(null); }}
                                style={{ padding: '8px 10px', fontSize: '0.84rem', cursor: 'pointer', borderBottom: '1px solid #EDE3D2', color: '#3A2E26' }}>
                                {p}
                              </div>
                            ))}
                            {typed && !hasExact && (
                              <div onMouseDown={(e) => { e.preventDefault(); setActiveField(null); }}
                                style={{ padding: '8px 10px', fontSize: '0.8rem', color: '#A0612A', fontStyle: 'italic', cursor: 'pointer' }}>
                                + Use "{typed}" as new pack/size
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      {activeField !== 'pack_size' && (() => {
                        const v = (createForm.pack_size || '').trim();
                        if (!v) return null;
                        const match = knownPackSizes.some(p => p.toLowerCase() === v.toLowerCase());
                        return (
                          <div style={{ fontSize: '0.68rem', marginTop: 3, color: match ? '#2F7A3E' : '#A0612A' }}>
                            {match ? '✓ Matches existing pack/size' : '+ Will create new pack/size'}
                          </div>
                        );
                      })()}
                    </div>
                    <div style={{ position: 'relative' }}>
                      <label style={{ display: 'block', fontSize: '0.72rem', color: '#5A4A42', marginBottom: 2 }}>Count unit</label>
                      <input type="text"
                        value={createForm.unit}
                        onChange={(e) => setCreateForm(f => ({ ...f, unit: e.target.value }))}
                        onFocus={() => setActiveField('unit')}
                        onBlur={() => setTimeout(() => setActiveField(a => a === 'unit' ? null : a), 150)}
                        placeholder="e.g. bottle, case, each"
                        autoComplete="off" autoCorrect="off" autoCapitalize="none" spellCheck={false}
                        style={{
                          width: '100%', padding: '7px 9px',
                          border: activeField === 'unit' ? '2px solid #8B6F47' : '1px solid #C8B5A0',
                          borderRadius: 6, fontSize: '0.82rem', outline: 'none',
                        }} />
                      {activeField === 'unit' && (() => {
                        const ranked = rankSuggestions(knownUnits, createForm.unit);
                        const typed = (createForm.unit || '').trim();
                        const hasExact = typed && knownUnits.some(u => u.toLowerCase() === typed.toLowerCase());
                        if (ranked.length === 0 && !typed) return null;
                        return (
                          <div style={{
                            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                            background: '#FFF8EE', border: '1px solid #C8B5A0', borderTop: 'none',
                            borderRadius: '0 0 6px 6px', maxHeight: 220, overflowY: 'auto',
                            boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
                          }}>
                            {ranked.map(u => (
                              <div key={u}
                                onMouseDown={(e) => { e.preventDefault(); setCreateForm(f => ({ ...f, unit: u })); setActiveField(null); }}
                                style={{ padding: '8px 10px', fontSize: '0.84rem', cursor: 'pointer', borderBottom: '1px solid #EDE3D2', color: '#3A2E26' }}>
                                {u}
                              </div>
                            ))}
                            {typed && !hasExact && (
                              <div onMouseDown={(e) => { e.preventDefault(); setActiveField(null); }}
                                style={{ padding: '8px 10px', fontSize: '0.8rem', color: '#A0612A', fontStyle: 'italic', cursor: 'pointer' }}>
                                + Use "{typed}" as new unit
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      {activeField !== 'unit' && (() => {
                        const v = (createForm.unit || '').trim();
                        if (!v) return null;
                        const match = knownUnits.some(u => u.toLowerCase() === v.toLowerCase());
                        return (
                          <div style={{ fontSize: '0.68rem', marginTop: 3, color: match ? '#2F7A3E' : '#A0612A' }}>
                            {match ? '✓ Matches existing unit' : '+ Will create new unit'}
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Conditional: PAR for order counts */}
                  {showParMoq && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 6, marginBottom: 8 }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.72rem', color: '#5A4A42', marginBottom: 2 }}>PAR level</label>
                        <input type="number" inputMode="decimal" step="any" value={createForm.par_level}
                          onChange={(e) => setCreateForm(f => ({ ...f, par_level: e.target.value }))}
                          placeholder="0"
                          style={{ width: '100%', padding: '7px 9px', border: '1px solid #C8B5A0', borderRadius: 6, fontSize: '0.82rem' }} />
                      </div>
                    </div>
                  )}

                  {/* Conditional: pricing for order + EOP */}
                  {showPrice && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 8 }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.72rem', color: '#5A4A42', marginBottom: 2 }}>Case price</label>
                        <input type="number" inputMode="decimal" step="0.01" value={createForm.case_price}
                          onChange={(e) => setCreateForm(f => ({ ...f, case_price: e.target.value }))}
                          placeholder="0.00"
                          style={{ width: '100%', padding: '7px 9px', border: '1px solid #C8B5A0', borderRadius: 6, fontSize: '0.82rem' }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.72rem', color: '#5A4A42', marginBottom: 2 }}>Each price</label>
                        <input type="number" inputMode="decimal" step="0.01" value={createForm.each_price}
                          onChange={(e) => setCreateForm(f => ({ ...f, each_price: e.target.value }))}
                          placeholder="0.00"
                          style={{ width: '100%', padding: '7px 9px', border: '1px solid #C8B5A0', borderRadius: 6, fontSize: '0.82rem' }} />
                      </div>
                    </div>
                  )}

                  <button style={{ ...btnPrimary, marginTop: 6, width: '100%' }} disabled={busy} onClick={createNewItem}>
                    {busy ? 'Saving…' : 'Create item & add to count'}
                  </button>
                </div>
              </div>
            )}

            {phase === 'done' && (
              <div>
                <div style={{ fontSize: '0.9rem', marginBottom: 12 }}>{info}</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={btnPrimary} onClick={() => { reset(); setPhase('scanning'); }}>Scan another</button>
                  <button style={btnGhost} onClick={onClose}>Close</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BarcodeScanAndAddModal;
